// src/components/PortfolioSummary.js
import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  PieChart, 
  Wallet, 
  AlertCircle,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const PortfolioSummary = ({ portfolio, darkMode }) => {
  // Calculate portfolio metrics
  const totalValue = portfolio.reduce((sum, stock) => sum + stock.marketValue, 0);
  const totalCost = portfolio.reduce((sum, stock) => sum + (stock.average_cost * stock.quantity), 0);
  const totalPnl = portfolio.reduce((sum, stock) => sum + stock.unrealizedPnl, 0);
  const totalPnlPercent = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;
  
  const dailyPnl = portfolio.reduce((sum, stock) => {
    const dailyChange = (stock.currentPrice - (stock.market_price || stock.currentPrice)) * stock.quantity;
    return sum + dailyChange;
  }, 0);

  const gainers = portfolio.filter(stock => stock.unrealizedPnl > 0).length;
  const losers = portfolio.filter(stock => stock.unrealizedPnl < 0).length;

  // Top performers
  const topGainer = portfolio.reduce((max, stock) => 
    stock.unrealizedPnl > max.unrealizedPnl ? stock : max, 
    { unrealizedPnl: -Infinity, symbol: 'N/A' }
  );

  const topLoser = portfolio.reduce((min, stock) => 
    stock.unrealizedPnl < min.unrealizedPnl ? stock : min, 
    { unrealizedPnl: Infinity, symbol: 'N/A' }
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const summaryCards = [
    {
      title: 'Total Portfolio Value',
      value: formatCurrency(totalValue),
      change: formatPercentage(totalPnlPercent),
      isPositive: totalPnlPercent >= 0,
      icon: DollarSign,
      color: 'blue'
    },
    {
      title: 'Total P&L',
      value: formatCurrency(totalPnl),
      change: formatPercentage(totalPnlPercent),
      isPositive: totalPnl >= 0,
      icon: totalPnl >= 0 ? TrendingUp : TrendingDown,
      color: totalPnl >= 0 ? 'green' : 'red'
    },
    {
      title: 'Daily P&L',
      value: formatCurrency(dailyPnl),
      change: dailyPnl !== 0 ? formatPercentage((dailyPnl / totalValue) * 100) : '0.00%',
      isPositive: dailyPnl >= 0,
      icon: dailyPnl >= 0 ? ArrowUp : ArrowDown,
      color: dailyPnl >= 0 ? 'green' : 'red'
    },
    {
      title: 'Holdings',
      value: portfolio.length,
      change: `${gainers} Gainers / ${losers} Losers`,
      isPositive: gainers >= losers,
      icon: PieChart,
      color: 'purple'
    }
  ];

  const getColorClasses = (color, isPositive) => {
    const baseColors = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', darkBg: 'bg-blue-900/20', darkText: 'text-blue-400' },
      green: { bg: 'bg-green-100', text: 'text-green-600', darkBg: 'bg-green-900/20', darkText: 'text-green-400' },
      red: { bg: 'bg-red-100', text: 'text-red-600', darkBg: 'bg-red-900/20', darkText: 'text-red-400' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', darkBg: 'bg-purple-900/20', darkText: 'text-purple-400' }
    };

    const colors = baseColors[color] || baseColors.blue;
    return darkMode ? {
      bg: colors.darkBg,
      text: colors.darkText
    } : {
      bg: colors.bg,
      text: colors.text
    };
  };

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {summaryCards.map((card, index) => {
          const Icon = card.icon;
          const colors = getColorClasses(card.color, card.isPositive);
          
          return (
            <div
              key={index}
              className={`rounded-lg p-6 transition-all hover:scale-105 ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              } shadow-lg`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className={`text-sm font-medium ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {card.title}
                  </p>
                  <p className="text-2xl font-bold mt-1">
                    {card.value}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${colors.bg}`}>
                  <Icon className={`h-6 w-6 ${colors.text}`} />
                </div>
              </div>
              
              <div className={`flex items-center text-sm ${
                card.isPositive 
                  ? darkMode ? 'text-green-400' : 'text-green-600'
                  : darkMode ? 'text-red-400' : 'text-red-600'
              }`}>
                {card.isPositive ? (
                  <ArrowUp className="h-4 w-4 mr-1" />
                ) : (
                  <ArrowDown className="h-4 w-4 mr-1" />
                )}
                {card.change}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div className={`mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      } rounded-lg p-6 shadow-lg`}>
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            topGainer.unrealizedPnl > 0 ? 
              darkMode ? 'text-green-400' : 'text-green-600' : 
              darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {topGainer.symbol}
          </div>
          <p className="text-sm text-gray-500">Top Gainer</p>
          {topGainer.unrealizedPnl > 0 && (
            <p className="text-sm text-green-600">
              +{formatCurrency(topGainer.unrealizedPnl)}
            </p>
          )}
        </div>

        <div className="text-center">
          <div className={`text-2xl font-bold ${
            topLoser.unrealizedPnl < 0 ? 
              darkMode ? 'text-red-400' : 'text-red-600' : 
              darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {topLoser.symbol}
          </div>
          <p className="text-sm text-gray-500">Top Loser</p>
          {topLoser.unrealizedPnl < 0 && (
            <p className="text-sm text-red-600">
              {formatCurrency(topLoser.unrealizedPnl)}
            </p>
          )}
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold">
            {formatCurrency(totalCost)}
          </div>
          <p className="text-sm text-gray-500">Total Invested</p>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSummary;